import { create } from 'zustand'
import type { ChatMessage, AIContext } from '@/types'
import { generateId } from '@/lib/utils'
import { claudeApi } from '@/lib/api'

interface ChatStore {
  messages: ChatMessage[]
  isStreaming: boolean
  context: AIContext
  setContext: (ctx: Partial<AIContext>) => void
  sendMessage: (content: string) => Promise<void>
  clearMessages: () => void
  addMessage: (msg: Omit<ChatMessage, 'id' | 'timestamp'>) => ChatMessage
}

export const useChatStore = create<ChatStore>()((set, get) => ({
  messages: [],
  isStreaming: false,
  context: {},

  setContext: (ctx) =>
    set((state) => ({ context: { ...state.context, ...ctx } })),

  addMessage: (msg) => {
    const full: ChatMessage = {
      ...msg,
      id: generateId(),
      timestamp: new Date().toISOString(),
    }
    set((state) => ({ messages: [...state.messages, full] }))
    return full
  },

  sendMessage: async (content) => {
    const { messages, context, addMessage } = get()

    addMessage({ role: 'user', content })

    const assistantMsg = addMessage({ role: 'assistant', content: '', isStreaming: true })

    set({ isStreaming: true })

    try {
      const history = messages.map((m) => ({ role: m.role, content: m.content }))
      history.push({ role: 'user', content })

      let accumulated = ''

      await claudeApi.chat(history, context, (chunk) => {
        accumulated += chunk
        set((state) => ({
          messages: state.messages.map((m) =>
            m.id === assistantMsg.id ? { ...m, content: accumulated } : m
          ),
        }))
      })

      set((state) => ({
        messages: state.messages.map((m) =>
          m.id === assistantMsg.id ? { ...m, isStreaming: false } : m
        ),
        isStreaming: false,
      }))
    } catch (err) {
      set((state) => ({
        messages: state.messages.map((m) =>
          m.id === assistantMsg.id
            ? { ...m, content: 'Sorry, I encountered an error. Please try again.', isStreaming: false }
            : m
        ),
        isStreaming: false,
      }))
      throw err
    }
  },

  clearMessages: () => set({ messages: [] }),
}))
